import { format } from 'date-fns';
import { FunctionComponent, useCallback, useContext, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ConfigContext, OrganisationContext } from '../../../context';
import { AnalyticChartPlatform, analyticsApi, OrganisationTagType } from '../../../services';
import {
  Button,
  ButtonType,
  cookieOptions,
  Filter,
  FilterValue,
  IconLabelSizes,
  LastDateValue,
  optArrItem,
  ORG_DASH_ANALYTICS,
  Select,
  SelectItem,
  TabItem,
  Tabs,
  useAppSelector,
  useDateRangeFilter,
  useFilter,
  useIsNarrowViewport,
  useLastDate,
} from '../../../shared';
import { Content } from './Content';
import { Other } from './Other';
import { Referral } from './Referral';
import { User } from './User';

import classNames from 'classnames';
import classes from './Analytics.module.scss';

export enum ChartType {
  CONTENT_AND_ENGAGEMENT = 'CONTENT_AND_ENGAGEMENT',
  CONTENT_WITH_INTERACTIONS = 'CONTENT_WITH_INTERACTIONS',
  CONTENT_TOP_5 = 'CONTENT_TOP_5',
  COMMENT_TOP_5 = 'COMMENT_TOP_5',
  CONTENT = 'CONTENT',
  CONTENT_TAGS = 'CONTENT_TAGS',
  CHANNELS = 'CHANNELS',
  MIXES = 'MIXES',
  PUSH_NOTIFICATIONS = 'PUSH_NOTIFICATIONS',
  IN_APP_NOTIFICATIONS = 'IN_APP_NOTIFICATIONS',
  ACTIVE_VS_ENGAGED_USERS = 'ACTIVE_VS_ENGAGED_USERS',
  ACTIVE_VS_ENGAGED_USERS_RATE = 'ACTIVE_VS_ENGAGED_USERS_RATE',
  ACTIVE_VS_ENGAGED_DEMO_USERS = 'ACTIVE_VS_ENGAGED_DEMO_USERS',
  ACTIVE_VS_ENGAGED_ALL_USERS = 'ACTIVE_VS_ENGAGED_ALL_USERS',
  USER_FUNNEL = 'USER_FUNNEL',
  USER_FUNNEL_V2 = 'USER_FUNNEL_V2',
  USER_FUNNEL_V3 = 'USER_FUNNEL_V3',
  USER_DEMO_FUNNEL = 'USER_DEMO_FUNNEL',
  USER_INTERACTIONS = 'USER_INTERACTIONS',
  USER_ROLE_CONTENT = 'USER_ROLE_CONTENT',
  SESSIONS_DURATION = 'SESSIONS_DURATION',
  SESSIONS_PER_DAY = 'SESSIONS_PER_DAY',
  SESSIONS_BY_DAY_OF_WEEK = 'SESSIONS_BY_DAY_OF_WEEK',
  SESSIONS_BY_HOUR_OF_DAY = 'SESSIONS_BY_HOUR_OF_DAY',
  ACTIVE_USERS_PER_MONTH = 'ACTIVE_USERS_PER_MONTH',
  LOGIN_LOGOUT = 'LOGIN_LOGOUT',
  INSTALL_UNINSTALL = 'INSTALL_UNINSTALL',
  USER_REFERRAL_ACTIVITY = 'USER_REFERRAL_ACTIVITY',
  USER_REFERRAL_LIST = 'USER_REFERRAL_LIST',
}

enum AnalyticsTabType {
  CONTENT,
  USER,
  REFERRAL,
  OTHER,
}

export const COLORS = [
  '#33AAFF',
  '#F471B6',
  '#00D7F3',
  '#FFDD33',
  '#7DBC60',
  '#EC8A44',
  '#F45C5C',
  '#833DA4',
  '#0077CC',
  '#52DC8B',
  '#F59E0B',
];

export const Analytics: FunctionComponent = () => {
  const { t } = useTranslation();

  const { analytics, referral } = { ...useContext(ConfigContext).config.elements };

  const { channelsExclude } = analytics;

  const { enabled: referralEnabled = false } = { ...referral };

  const { tags, categories } = useContext(OrganisationContext).organisation;

  const [analyticGenerateContentCSVReport] =
    analyticsApi.endpoints.analyticGenerateContentCSVReport.useMutation();

  const [analyticGenerateUserCSVReport] =
    analyticsApi.endpoints.analyticGenerateUserCSVReport.useMutation();

  const [analyticGenerateUserReferralCSVReport] =
    analyticsApi.endpoints.analyticGenerateUserReferralCSVReport.useMutation();

  const [analyticGenerateOtherCSVReport] =
    analyticsApi.endpoints.analyticGenerateOtherCSVReport.useMutation();

  const { isLastDate, getLastDate } = useLastDate();

  const { channels } = useAppSelector(({ channels }) => channels);

  const [cookies, setCookie] = useCookies([ORG_DASH_ANALYTICS]);

  const isNarrowViewport = useIsNarrowViewport();

  const defaultDate = LastDateValue.LAST_7_DAYS;

  const {
    dateRange = defaultDate,
    channelsId = [],
    categoryId = [],
    internalTagsId = [],
    externalTagsId = [],
    externalStatusTagsId = [],
    platform = [
      AnalyticChartPlatform.ANDROID,
      AnalyticChartPlatform.IOS,
      AnalyticChartPlatform.WEB,
    ],
    filter = [{ groupId: 'date', groupItems: [{ id: defaultDate, value: defaultDate }] }],
    selectedTabIndex = AnalyticsTabType.CONTENT,
    userActiveYear = new Date().getFullYear(),
    storyCardType = [],
  } = { ...cookies[ORG_DASH_ANALYTICS] };

  const channelsIdFiltered = useMemo(
    () => channelsId.filter((id: number) => !channelsExclude.includes(id)),
    [channelsExclude, channelsId]
  );

  const channelItems = useMemo(() => {
    return channels
      .filter(({ id }) => !channelsExclude.includes(id))
      .map(({ id, name }) => ({ id, title: name }));
  }, [channels, channelsExclude]);

  const categoryItems = useMemo(() => {
    return categories.map(({ id, name }) => ({ id, title: name }));
  }, [categories]);

  const getTagsItems = useCallback(
    (tagsType: OrganisationTagType): SelectItem<number | null>[] => {
      const items = tags
        .filter(({ type }) => type === tagsType)
        .map(({ id, name }) => ({ id, title: name }));

      if (!items.length) {
        return [];
      }

      return [{ id: null, title: t(`organisationTags.exclude-${tagsType}`) }, ...items];
    },
    [t, tags]
  );

  const internalTagsItems = useMemo(
    () => getTagsItems(OrganisationTagType.INTERNAL),
    [getTagsItems]
  );

  const externalTagsItems = useMemo(
    () => getTagsItems(OrganisationTagType.EXTERNAL),
    [getTagsItems]
  );

  const externalStatusTagsItems = useMemo(
    () => getTagsItems(OrganisationTagType.EXTERNAL_STATUS),
    [getTagsItems]
  );

  const organisationTags = useMemo(() => {
    return {
      internalTagsId: internalTagsId ?? [null],
      externalTagsId: externalTagsId ?? [null],
      externalStatusTagsId: externalStatusTagsId ?? [null],
    };
  }, [externalTagsId, externalStatusTagsId, internalTagsId]);

  const { group: dateRangeGroup } = useDateRangeFilter({ filter, allowReset: false });

  const { getDateRangeValue } = useFilter();

  const lastDate = useMemo(() => isLastDate(dateRange), [dateRange, isLastDate]);

  const { from = '', to = '' } = lastDate ? getLastDate(dateRange) : { ...dateRange };

  const dateFilterLabel = useMemo(() => {
    if (lastDate) {
      return t(`dateRangeFilter.${dateRange}`);
    }
    return `${format(new Date(from), 'dd.MM.yy')} - ${format(new Date(to), 'dd.MM.yy')}`;
  }, [dateRange, from, lastDate, t, to]);

  const filterChange = useCallback(
    (filter: FilterValue[]) => {
      const { dateRange } = getDateRangeValue({ filter, groupId: dateRangeGroup.id });

      setCookie(
        ORG_DASH_ANALYTICS,
        { ...cookies[ORG_DASH_ANALYTICS], dateRange, filter },
        cookieOptions()
      );
    },
    [cookies, dateRangeGroup.id, getDateRangeValue, setCookie]
  );

  const channelChange = useCallback(
    (channelsId: number[]) => {
      setCookie(
        ORG_DASH_ANALYTICS,
        { ...cookies[ORG_DASH_ANALYTICS], channelsId },
        cookieOptions()
      );
    },
    [cookies, setCookie]
  );

  const categoryChange = useCallback(
    (categoryId: number[]) => {
      setCookie(
        ORG_DASH_ANALYTICS,
        { ...cookies[ORG_DASH_ANALYTICS], categoryId },
        cookieOptions()
      );
    },
    [cookies, setCookie]
  );

  const internalTagsChange = useCallback(
    (internalTagsId: (number | null)[]) => {
      setCookie(
        ORG_DASH_ANALYTICS,
        { ...cookies[ORG_DASH_ANALYTICS], internalTagsId },
        cookieOptions()
      );
    },
    [cookies, setCookie]
  );

  const externalTagsChange = useCallback(
    (externalTagsId: (number | null)[]) => {
      setCookie(
        ORG_DASH_ANALYTICS,
        { ...cookies[ORG_DASH_ANALYTICS], externalTagsId },
        cookieOptions()
      );
    },
    [cookies, setCookie]
  );

  const externalStatusTagsChange = useCallback(
    (externalStatusTagsId: (number | null)[]) => {
      setCookie(
        ORG_DASH_ANALYTICS,
        { ...cookies[ORG_DASH_ANALYTICS], externalStatusTagsId },
        cookieOptions()
      );
    },
    [cookies, setCookie]
  );

  const tabIndexChange = useCallback(
    (selectedTabIndex: number) => {
      setCookie(
        ORG_DASH_ANALYTICS,
        { ...cookies[ORG_DASH_ANALYTICS], selectedTabIndex },
        cookieOptions()
      );
    },
    [cookies, setCookie]
  );

  const renderChannelFilter = useMemo(() => {
    if (selectedTabIndex !== AnalyticsTabType.CONTENT) {
      return null;
    }

    return (
      <Select
        label={t('common.channels_other')}
        className={classes['analytics__tab-content-filters-select']}
        selectedItemIds={channelsIdFiltered}
        items={channelItems}
        onMultiCheckChange={channelChange}
        disabled={channelItems.length === 1}
      />
    );
  }, [channelChange, channelItems, channelsIdFiltered, selectedTabIndex, t]);

  const renderCategoryFilter = useMemo(() => {
    if (selectedTabIndex !== AnalyticsTabType.CONTENT || !Boolean(categories.length)) {
      return null;
    }

    return (
      <Select
        label={t(`cardCategoryFilter.title`)}
        className={classes['analytics__tab-content-filters-select']}
        selectedItemIds={categoryId}
        items={categoryItems}
        onMultiCheckChange={categoryChange}
      />
    );
  }, [categories.length, categoryChange, categoryId, categoryItems, selectedTabIndex, t]);

  const renderTagsFilter = useMemo(() => {
    if (selectedTabIndex !== AnalyticsTabType.CONTENT) {
      return null;
    }

    return (
      <>
        {Boolean(internalTagsItems.length) && (
          <Select
            label={t(`organisationTags.title-INTERNAL`)}
            className={classes['analytics__tab-content-filters-select']}
            selectedItemIds={organisationTags.internalTagsId}
            items={internalTagsItems}
            onMultiCheckChange={internalTagsChange}
          />
        )}

        {Boolean(externalTagsItems.length) && (
          <Select
            label={t(`organisationTags.title-EXTERNAL`)}
            className={classes['analytics__tab-content-filters-select']}
            selectedItemIds={organisationTags.externalTagsId}
            items={externalTagsItems}
            onMultiCheckChange={externalTagsChange}
          />
        )}

        {Boolean(externalStatusTagsItems.length) && (
          <Select
            label={t(`organisationTags.title-EXTERNAL_STATUS`)}
            className={classes['analytics__tab-content-filters-select']}
            selectedItemIds={organisationTags.externalStatusTagsId}
            items={externalStatusTagsItems}
            onMultiCheckChange={externalStatusTagsChange}
          />
        )}
      </>
    );
  }, [
    externalStatusTagsChange,
    externalStatusTagsItems,
    externalTagsChange,
    externalTagsItems,
    internalTagsChange,
    internalTagsItems,
    organisationTags,
    selectedTabIndex,
    t,
  ]);

  const tabFilters = useMemo(() => {
    return (
      <>
        <Filter
          iconId={'down-arrow'}
          label={dateFilterLabel}
          labelSize={IconLabelSizes.large}
          groups={[dateRangeGroup]}
          filter={filter}
          onChange={filterChange}
        />

        {renderChannelFilter}

        {renderCategoryFilter}

        {renderTagsFilter}
      </>
    );
  }, [
    dateFilterLabel,
    dateRangeGroup,
    filter,
    filterChange,
    renderCategoryFilter,
    renderChannelFilter,
    renderTagsFilter,
  ]);

  const getCSVReport = useMemo(() => {
    switch (selectedTabIndex) {
      case AnalyticsTabType.CONTENT:
        return analyticGenerateContentCSVReport;
      case AnalyticsTabType.USER:
        return analyticGenerateUserCSVReport;
      case AnalyticsTabType.REFERRAL:
        return analyticGenerateUserReferralCSVReport;
      default:
        return analyticGenerateOtherCSVReport;
    }
  }, [
    analyticGenerateContentCSVReport,
    analyticGenerateOtherCSVReport,
    analyticGenerateUserCSVReport,
    analyticGenerateUserReferralCSVReport,
    selectedTabIndex,
  ]);

  const exportTabCharts = useCallback(async () => {
    const sent = await getCSVReport({
      from,
      to,
      channelId: channelsIdFiltered,
      platform,
      categoryId,
      organisationTags,
      yearUserSessionPerMonth: userActiveYear,
      storyCardType,
    }).unwrap();

    toast(t(`common.${sent ? 'report-sent' : 'error-message'}`));
  }, [
    categoryId,
    channelsIdFiltered,
    from,
    getCSVReport,
    organisationTags,
    platform,
    storyCardType,
    t,
    to,
    userActiveYear,
  ]);

  const tabContent = useCallback(
    (tabChart: JSX.Element) => {
      return (
        <div className={classes['analytics__tab-content']}>
          <div className={classes['analytics__tab-content-filters']}>{tabFilters}</div>
          {tabChart}
        </div>
      );
    },
    [tabFilters]
  );

  const tabItems: TabItem[] = useMemo(
    () => [
      {
        index: AnalyticsTabType.CONTENT,
        name: t('analyticsTabs.content'),
        content: tabContent(
          <Content
            from={from}
            to={to}
            channelId={channelsIdFiltered}
            platform={platform}
            categoryId={categoryId}
            organisationTags={organisationTags}
          />
        ),
      },
      {
        index: AnalyticsTabType.USER,
        name: t('analyticsTabs.user'),
        content: tabContent(<User from={from} to={to} platform={platform} />),
      },
      ...optArrItem(referralEnabled, {
        index: AnalyticsTabType.REFERRAL,
        name: t('analyticsTabs.referral'),
        content: tabContent(<Referral from={from} to={to} />),
      }),
      {
        index: AnalyticsTabType.OTHER,
        name: t('analyticsTabs.other'),
        content: tabContent(<Other from={from} to={to} />),
      },
    ],
    [
      categoryId,
      channelsIdFiltered,
      from,
      organisationTags,
      platform,
      referralEnabled,
      t,
      tabContent,
      to,
    ]
  );

  return (
    <div
      className={classNames(classes['analytics'], {
        [classes['analytics--narrow']]: isNarrowViewport,
      })}
    >
      <h1 className={classes['analytics__header']}>{t('adminLayoutMenu.analytics')}</h1>
      <Button
        className={classes['analytics__export']}
        label={t('common.export')}
        type={ButtonType.primary}
        onClick={exportTabCharts}
      />
      <Tabs
        items={tabItems}
        selectedTabIndex={selectedTabIndex}
        setSelectedTabIndex={tabIndexChange}
        noBackground
      />
    </div>
  );
};
